/**
 * @render react
 * @name Ceros
 */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import './_style.scss';

/**
 * Ceros component. This platform allows users to design and create pages to be exported in an iframe and embedded on other sites.
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {string} domain - Value for the data-ceros-origin-domains attribute of the JS tag. Default value: view.ceros.com
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {string} html - HTML code from Ceros
 * @param {string} javascriptUrl - JS needed for Ceros. Default value: //view.ceros.com/scroll-proxy.min.js
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 */
const Ceros = ({
  context: { cssPrefix },
  domain,
  extraClassNames,
  html,
  javascriptUrl,
  loadStyles,
}) => {
  useEffect(() => {
    if (!document.getElementById('ceros-javascript')) {
      // inject script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = javascriptUrl;
      script.id = 'ceros-javascript';
      script.setAttribute('data-ceros-origin-domains', domain);
      document.body.appendChild(script);
    }

    return () => {
      if (typeof window !== 'undefined') {
        const script = document.getElementById('ceros-javascript');
        if (script) {
          document.body.removeChild(script);
        }
      }
    };
  }, []);

  return (
    <div
      className={classNames({
        [`${cssPrefix}__ceros`]: loadStyles,
        ...extraClassNames,
      })}
      dangerouslySetInnerHTML={{ __html: html }} // eslint-disable-line
    />
  );
};

Ceros.propTypes = {
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  domain: PropTypes.string,
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  html: PropTypes.string,
  javascriptUrl: PropTypes.string,
  loadStyles: PropTypes.bool,
};

Ceros.defaultProps = {
  context: {},
  domain: 'view.ceros.com',
  extraClassNames: {},
  html: null,
  javascriptUrl: '//view.ceros.com/scroll-proxy.min.js',
  loadStyles: true,
};

export default withPolaris(Ceros);
